import React from 'react';
import { Link } from 'react-router-dom';
import { SkewButton, Card, GradientDivider } from '../components/UIComponents';
import { Calendar, Heart, ShoppingBag, Users, Zap, Flame, ArrowRight } from 'lucide-react';

const CLUB_DEUCES_LOGO = "https://customer-assets.emergentagent.com/job_goalgeous-hub/artifacts/cul9k56e_Blue%20Playful%20Closed%20Due%20To%20Weather%20Facebook%20Post.png";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1717500251805-cd0306a48de4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxncm91cCUyMGZpdG5lc3MlMjBjbGFzcyUyMHdvbWVuJTIwZGl2ZXJzZSUyMGVuZXJnZXRpYyUyMGd5bXxlbnwwfHx8fDE3NzA3NjM1OTV8MA&ixlib=rb-4.1.0&q=85",
  vibe: "https://images.unsplash.com/photo-1623804421482-721b3901c125?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxuZW9uJTIwZ3ltJTIwbGlnaHRpbmclMjBlbmVyZ2V0aWMlMjBmaXRuZXNzJTIwYXRtb3NwaGVyZXxlbnwwfHx8fDE3NzA3NjM2MTJ8MA&ixlib=rb-4.1.0&q=85",
};

const actionTiles = [
  {
    icon: Calendar,
    title: "Book Classes",
    description: "Join our high-energy step classes and group workouts",
    cta: "Book Now",
    link: "https://viagro.com",
    color: "lime"
  },
  {
    icon: ShoppingBag,
    title: "Shop Hats & Shirts",
    description: "Rep the club with official Club Deuces gear",
    cta: "Shop Now",
    link: "https://viagro.com",
    color: "pink"
  },
  {
    icon: Heart,
    title: "Book Assisted Stretching",
    description: "One-on-one sessions for recovery and flexibility",
    cta: "Book Session",
    link: "https://viagro.com",
    color: "lime"
  }
];

export const ClubDeucesPage = () => {
  return (
    <div data-testid="clubdeuces-page" className="bg-brand-black min-h-screen">
      {/* Custom Club Deuces Header */}
      <header data-testid="clubdeuces-header" className="fixed top-0 w-full z-50 bg-brand-black/95 backdrop-blur-md border-b border-brand-lime/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-24">
            <Link to="/club-deuces" data-testid="clubdeuces-logo">
              <img 
                src={CLUB_DEUCES_LOGO} 
                alt="Club Deuces" 
                className="h-20 w-auto"
              />
            </Link>
          </div>
        </div>
        {/* Navigation back to main site */}
        <div className="bg-brand-dark/50 py-2">
          <div className="max-w-7xl mx-auto px-4 flex justify-center gap-6">
            <Link to="/" className="text-xs font-oswald uppercase tracking-widest text-brand-gray hover:text-brand-lime transition-colors">
              ← Back to Power Up Your Fitness
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section data-testid="clubdeuces-hero" className="relative pt-36 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={IMAGES.hero} 
            alt="Club Deuces" 
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-black via-brand-black/80 to-brand-black" />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h1 className="font-oswald text-6xl md:text-8xl font-bold uppercase tracking-tighter text-white mb-6">
            WHO YOU WITH?<br />
            <span className="text-brand-lime">DEUCES ✌🏾</span>
          </h1>
          <div className="w-32 h-1 bg-gradient-to-r from-brand-lime to-brand-pink mx-auto mb-8" />
          <p className="font-montserrat text-xl md:text-2xl text-brand-gray leading-relaxed mb-10 max-w-2xl mx-auto">
            Step with the club. Find your people. Transform together.
          </p>
          <SkewButton href="https://viagro.com" testId="clubdeuces-hero-cta" className="text-lg px-10 py-4">
            Book Club Deuces Classes
          </SkewButton>
        </div>
      </section>

      {/* Action Tiles Section */}
      <section data-testid="action-tiles-section" className="py-20 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {actionTiles.map((tile, index) => (
              <a
                key={tile.title}
                href={tile.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`action-tile-${index}`}
                className="group"
              >
                <Card className={`p-8 h-full text-center border-2 ${tile.color === 'lime' ? 'hover:border-brand-lime' : 'hover:border-brand-pink'} transition-all duration-300`}>
                  <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center transition-colors ${tile.color === 'lime' ? 'bg-brand-lime/10 group-hover:bg-brand-lime/20' : 'bg-brand-pink/10 group-hover:bg-brand-pink/20'}`}>
                    <tile.icon size={40} className={tile.color === 'lime' ? 'text-brand-lime' : 'text-brand-pink'} />
                  </div>
                  <h3 className="font-oswald text-2xl uppercase text-white mb-3 group-hover:text-brand-lime transition-colors">
                    {tile.title}
                  </h3>
                  <p className="font-montserrat text-brand-gray mb-6">
                    {tile.description}
                  </p>
                  <div className={`inline-flex items-center gap-2 font-oswald uppercase tracking-wider ${tile.color === 'lime' ? 'text-brand-lime' : 'text-brand-pink'}`}>
                    <span>{tile.cta}</span>
                    <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform" />
                  </div>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Class Schedule Section */}
      <section data-testid="class-schedule-section" className="py-20 bg-brand-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <p className="font-oswald text-sm uppercase tracking-[0.3em] text-brand-lime mb-4">
              Join The Movement
            </p>
            <h2 className="font-oswald text-4xl md:text-6xl font-bold uppercase tracking-tighter text-white">
              Class Schedule
            </h2>
          </div>
          
          {/* ALL LEVELS STEP Class */}
          <div className="bg-brand-dark border-2 border-brand-lime p-8 md:p-12 relative overflow-hidden max-w-4xl mx-auto">
            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-lime/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-brand-pink/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10 text-center">
              <h3 className="font-oswald text-4xl md:text-5xl uppercase text-white mb-4">
                ALL LEVELS STEP
              </h3>
              <div className="flex items-center justify-center gap-6 mb-6">
                <span className="flex items-center gap-2 text-brand-lime font-oswald text-xl uppercase tracking-wider">
                  <Calendar size={24} />
                  Mondays
                </span>
                <span className="text-brand-gray">•</span>
                <span className="flex items-center gap-2 text-brand-pink font-oswald text-xl uppercase tracking-wider">
                  7:15 PM
                </span>
              </div>
              
              <p className="font-montserrat text-lg text-brand-gray leading-relaxed max-w-2xl mx-auto mb-8">
                A high-energy step class for all fitness levels. Come learn, move, sweat, and build confidence in a supportive environment. Perfect for beginners and experienced steppers.
              </p>
              
              <SkewButton href="https://viagro.com" testId="book-step-class">
                Book Your Spot
              </SkewButton>
            </div>
          </div>
        </div>
      </section>

      {/* Merchandise Section */}
      <section data-testid="merch-section" className="py-20 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <p className="font-oswald text-sm uppercase tracking-[0.3em] text-brand-pink mb-4">
              Rep The Club
            </p>
            <h2 className="font-oswald text-4xl md:text-6xl font-bold uppercase tracking-tighter text-white mb-4">
              Club Deuces Merch
            </h2>
            <p className="font-montserrat text-brand-gray max-w-xl mx-auto">
              Show the world who you're with. Official Club Deuces gear for the movement.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Trucker Hats */}
            <Card testId="merch-hats" className="p-8 text-center group hover:border-brand-lime transition-all">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-brand-lime/10 flex items-center justify-center group-hover:bg-brand-lime/20 transition-colors">
                <span className="text-5xl">🧢</span>
              </div>
              <h3 className="font-oswald text-2xl uppercase text-white mb-2">Club Deuces Hats</h3>
              <p className="font-montserrat text-brand-gray mb-2">Trucker Hats</p>
              <p className="font-oswald text-2xl text-brand-lime mb-6">$28</p>
              <SkewButton href="https://viagro.com" testId="shop-hats">
                Shop Hats
              </SkewButton>
            </Card>

            {/* T-Shirts */}
            <Card testId="merch-shirts" className="p-8 text-center group hover:border-brand-pink transition-all">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-brand-pink/10 flex items-center justify-center group-hover:bg-brand-pink/20 transition-colors">
                <span className="text-5xl">👕</span>
              </div>
              <h3 className="font-oswald text-2xl uppercase text-white mb-2">Club Deuces T-Shirts</h3>
              <p className="font-montserrat text-brand-gray mb-2">Official Tees</p>
              <p className="font-oswald text-2xl text-brand-pink mb-6">$30</p>
              <SkewButton variant="secondary" href="https://viagro.com" testId="shop-shirts">
                Shop Shirts
              </SkewButton>
            </Card>
          </div>
        </div>
      </section>

      {/* Assisted Stretching Section */}
      <section data-testid="stretch-section" className="py-20 bg-brand-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="bg-gradient-to-r from-brand-dark to-brand-black border border-white/10 p-8 md:p-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-lime/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            
            <div className="relative z-10 max-w-3xl mx-auto text-center">
              <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-brand-lime/10 flex items-center justify-center">
                <Heart size={40} className="text-brand-lime" />
              </div>
              
              <p className="font-oswald text-sm uppercase tracking-[0.3em] text-brand-lime mb-4">
                Recovery & Flexibility
              </p>
              <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-tighter text-white mb-6">
                Assisted Stretch Sessions
              </h2>
              
              <p className="font-montserrat text-lg text-brand-gray leading-relaxed mb-8 max-w-2xl mx-auto">
                One-on-one assisted stretching to support recovery, flexibility, and performance. Let us help your body recover and move better.
              </p>
              
              <div className="mb-8">
                <span className="font-oswald text-3xl text-brand-lime">Starting at $30</span>
                <span className="font-montserrat text-brand-gray ml-2">per session</span>
              </div>
              
              <SkewButton href="https://viagro.com" testId="book-stretch-session">
                Book a Stretch Session
              </SkewButton>
            </div>
          </div>
        </div>
      </section>

      {/* Community Vibe Section */}
      <section data-testid="vibe-section" className="py-20 bg-brand-dark relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-20">
          <img 
            src={IMAGES.vibe} 
            alt="Club atmosphere" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-brand-dark" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-xl">
            <p className="font-oswald text-sm uppercase tracking-[0.3em] text-brand-pink mb-4">
              More Than Classes
            </p>
            <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-tighter text-white mb-6">
              The Deuces Vibe
            </h2>
            <GradientDivider />
            <p className="font-montserrat text-lg text-brand-gray leading-relaxed mt-8 mb-8">
              Club Deuces isn't just about the workout. It's about the energy. The music. The community. When you walk in, you're family. When you leave, you're unstoppable.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-brand-lime/20 flex items-center justify-center">
                  <Zap size={20} className="text-brand-lime" />
                </div>
                <span className="font-montserrat text-white">High-energy playlists</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-brand-pink/20 flex items-center justify-center">
                  <Users size={20} className="text-brand-pink" />
                </div>
                <span className="font-montserrat text-white">Supportive community</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-brand-lime/20 flex items-center justify-center">
                  <Flame size={20} className="text-brand-lime" />
                </div>
                <span className="font-montserrat text-white">Results-driven workouts</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="clubdeuces-cta" className="py-24 bg-brand-black">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <img 
            src={CLUB_DEUCES_LOGO} 
            alt="Club Deuces" 
            className="h-32 mx-auto mb-8"
          />
          <h2 className="font-oswald text-5xl md:text-7xl font-bold uppercase text-white mb-6">
            WHO YOU WITH?<br />
            <span className="text-brand-lime">DEUCES ✌🏾</span>
          </h2>
          <p className="font-montserrat text-xl text-brand-gray mb-10">
            Step with the club. Find your people. Transform together.
          </p>
          <SkewButton href="https://viagro.com" testId="clubdeuces-final-cta" className="text-lg px-10 py-4">
            Join Club Deuces
          </SkewButton>
        </div>
      </section>

      {/* Footer Link back to main site */}
      <div className="bg-brand-dark py-6 text-center border-t border-white/10">
        <Link to="/" className="font-oswald text-sm uppercase tracking-widest text-brand-gray hover:text-brand-lime transition-colors">
          ← Return to Power Up Your Fitness
        </Link>
      </div>
    </div>
  );
};
